/**
 * RecentProvidersCard
 *
 * Testing tool: the most recently added Providers, newest first. The main
 * Providers list used to be ordered by id DESC, which doubled as admin
 * feedback on what uploads / manual adds had just created; it is now ordered
 * by captured claims, so that feedback lives here instead.
 *
 * Providers has no creation timestamp, so "recent" means highest id — the same
 * ordering the old list used. There is no date column and no time window.
 *
 * How a provider was created is INFERRED from its client links: affiliation
 * uploads, manual adds with a client selected and activity-note imports all
 * create a ProviderClients row; the monthly NPPES import never does. The
 * "Only client-linked" toggle is applied server-side (so it returns the newest
 * N linked providers, not N rows of which most are NPPES), and the Client
 * Added column shows every client the provider is linked to.
 *
 * Data comes from GET /v2/qa/recent-providers (admin-only, cross-client). The
 * whole set is returned in one shot; filtering / sorting / paging are
 * client-side, and DynamicTable gets a pre-sliced { rows, count } page.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SpecialtyUtil } from 'doctivity-shared/utils';
import axiosInstance from 'utils/axiosUtil';
import { DynamicTable } from 'components';

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    flex: 1,
  },
  description: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  limitSelect: {
    minWidth: 140,
  },
  summary: {
    marginLeft: 'auto',
    fontSize: 13,
    color: theme.palette.text.secondary,
    textAlign: 'right',
  },
  summaryCount: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.primary.main,
  },
  clientSep: {
    color: '#999999',
  },
  noLink: {
    color: '#999999',
    fontStyle: 'italic',
  },
  inactive: {
    marginLeft: 6,
    fontSize: 10,
    fontWeight: 600,
    color: '#8d6e00',
  },
  errorMsg: {
    fontSize: 13,
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
});

const LIMIT_OPTIONS = [100, 250, 500, 1000];
const DEFAULT_LIMIT = 250;

// Fresh object every call — DynamicTable's onOrderChange mutates query.order
// in place, so a shared literal would leak sort state across mounts.
function defaultQuery() {
  return {
    limit: 25,
    offset: 0,
    order: [['id', 'DESC']],
  };
}

// query.order entries are ['col', 'ASC'] for flat keys and
// ['a', 'b', 'ASC'] for dotted paths — mirror DynamicTable's own decoding.
function decodeOrder(order) {
  if (!Array.isArray(order)) return [];
  return order
    .filter((o) => Array.isArray(o) && o.length >= 2)
    .map((o) => ({
      key: o.slice(0, o.length - 1).join('.'),
      dir: String(o[o.length - 1]).toUpperCase() === 'DESC' ? -1 : 1,
    }));
}

function compareValues(a, b) {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1; // empties always sink
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

// DynamicTable writes column filters into query.where as { $like: '%v%' },
// { $eq: v } or { $match: v }. Apply them locally, case-insensitively.
function matchesFilter(value, filter) {
  if (filter === undefined || filter === null) return true;
  const text = value === null || value === undefined ? '' : String(value).toLowerCase();
  if (typeof filter !== 'object') {
    return text.includes(String(filter).toLowerCase());
  }
  if (filter.$eq !== undefined) {
    return text === String(filter.$eq).toLowerCase();
  }
  const needle = filter.$like ?? filter.$match;
  if (needle === undefined) return true;
  return text.includes(String(needle).replace(/^%|%$/g, '').toLowerCase());
}

// Flatten a server row into sortable / filterable string fields.
function decorate(row) {
  const clients = row.clients || [];
  return {
    ...row,
    specialty: row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
    clients_text: clients.map((c) => c.name).join(', '),
    source: clients.length > 0 ? 'Client-linked' : 'No client link',
  };
}

class RecentProvidersCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      rows: [],
      limit: DEFAULT_LIMIT,
      // NPPES imports can add thousands of providers at once and bury the
      // upload / manual adds this card exists to surface, so start filtered.
      linkedOnly: true,
      query: defaultQuery(),
    };
    // Monotonic request id so a slow earlier response can't overwrite a newer
    // one after the settings change.
    this.requestSeq = 0;
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchData();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onQueryChange = (query) => {
    this.setState({ query });
  };

  onLimitChange = (event) => {
    this.setState({ limit: event.target.value }, this.fetchData);
  };

  onLinkedOnlyChange = (event) => {
    this.setState({ linkedOnly: event.target.checked }, this.fetchData);
  };

  fetchData = async () => {
    const { user } = this.props;
    if (!user?.is_admin) return;

    const { limit, linkedOnly } = this.state;
    this.requestSeq += 1;
    const seq = this.requestSeq;
    const isCurrent = () => this.mounted && seq === this.requestSeq;

    this.setState((prev) => ({
      loading: true,
      error: null,
      query: { ...prev.query, offset: 0 },
    }));

    try {
      const res = await axiosInstance.get('/v2/qa/recent-providers', {
        params: { limit, linkedOnly },
      });
      if (!isCurrent()) return;
      this.setState({
        loading: false,
        rows: (res.data?.rows || []).map(decorate),
      });
    } catch (err) {
      if (!isCurrent()) return;
      this.setState({
        loading: false,
        rows: [],
        error: err.response?.data?.error || err.message || 'Could not load providers',
      });
    }
  };

  // Filter -> sort -> slice. DynamicTable renders exactly what it is handed.
  visibleRows() {
    const { rows, query } = this.state;
    const where = query.where || {};
    const filterKeys = Object.keys(where);

    let filtered = rows;
    if (filterKeys.length > 0) {
      filtered = filtered.filter((r) =>
        filterKeys.every((key) => matchesFilter(r[key], where[key])),
      );
    }

    const sorts = decodeOrder(query.order);
    if (sorts.length > 0) {
      filtered = filtered.slice().sort((a, b) => {
        for (let i = 0; i < sorts.length; i += 1) {
          const { key, dir } = sorts[i];
          const cmp = compareValues(a[key], b[key]);
          if (cmp !== 0) return cmp * dir;
        }
        return 0;
      });
    }

    const offset = query.offset || 0;
    return {
      count: filtered.length,
      rows: filtered.slice(offset, offset + query.limit),
    };
  }

  columns() {
    const { classes } = this.props;
    return [
      {
        label: 'ID',
        key: 'id',
        style: { width: 80 },
        showFilter: false,
      },
      {
        label: 'Last Name',
        key: 'last_name',
        style: { minWidth: 100 },
        format: (value, row) => (
          <>
            {value || row.business_name || `Provider ${row.id}`}
            {row.inactive_at && <span className={classes.inactive}>INACTIVE</span>}
          </>
        ),
      },
      {
        label: 'First Name',
        key: 'first_name',
        style: { minWidth: 100 },
      },
      {
        label: 'Credential',
        key: 'credential',
        style: { width: 90 },
      },
      {
        label: 'NPI',
        key: 'npi',
        style: { width: 100 },
      },
      {
        label: 'Specialty',
        key: 'specialty',
        style: { minWidth: 160 },
      },
      {
        label: 'City',
        key: 'city',
        style: { minWidth: 100 },
      },
      {
        label: 'State',
        key: 'state',
        style: { width: 50 },
        autoCapitalize: true,
      },
      {
        label: 'Client Added',
        key: 'clients_text',
        style: { minWidth: 200 },
        format: (value, row) => {
          const clients = row.clients || [];
          if (clients.length === 0) {
            return <span className={classes.noLink}>none — likely NPPES import</span>;
          }
          return (
            <>
              {clients.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && <span className={classes.clientSep}>, </span>}
                  {c.name}
                </React.Fragment>
              ))}
            </>
          );
        },
      },
    ];
  }

  render() {
    const { classes, user } = this.props;
    const { loading, error, rows, limit, linkedOnly, query } = this.state;

    if (!user?.is_admin) return null;

    const data = this.visibleRows();
    const newestId = rows.length > 0 ? rows[0].id : null;

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <div className={classes.cardInnerHeader}>
            <Typography
              component='h2'
              variant='h6'
              color='primary'
              gutterBottom
              className={classes.cardTitle}
            >
              Recent Added Providers
            </Typography>
            <Button
              color='primary'
              size='small'
              onClick={this.fetchData}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Icon fontSize='small'>refresh</Icon>
                )
              }
            >
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>

          <Typography className={classes.description}>
            The newest providers in Doctivity across all clients, newest first
            (by provider id — providers have no created date). Affiliation
            uploads, manual adds and activity-note imports link a provider to a
            client; the NPPES import does not, so unlinked providers are hidden
            by default.
          </Typography>

          <div className={classes.controls}>
            <FormControl size='small' className={classes.limitSelect}>
              <InputLabel>Show newest</InputLabel>
              <Select
                value={limit}
                label='Show newest'
                onChange={this.onLimitChange}
                disabled={loading}
              >
                {LIMIT_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n.toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={linkedOnly}
                  onChange={this.onLinkedOnlyChange}
                  disabled={loading}
                />
              }
              label={
                <Typography style={{ fontSize: 13 }}>
                  Only client-linked (hide NPPES imports)
                </Typography>
              }
            />

            {!loading && (
              <div className={classes.summary}>
                <span className={classes.summaryCount}>
                  {data.count.toLocaleString()}
                </span>
                {` provider${data.count === 1 ? '' : 's'}`}
                {newestId && (
                  <div style={{ fontSize: 11 }}>
                    {`newest id ${newestId.toLocaleString()}`}
                  </div>
                )}
              </div>
            )}
          </div>

          {error && <Typography className={classes.errorMsg}>{error}</Typography>}

          <DynamicTable
            ContainerType={React.Fragment}
            columns={this.columns()}
            data={loading ? { placeholder: true, showLoading: true } : data}
            idKey='id'
            query={query}
            onQueryChange={this.onQueryChange}
            getRowHref={(row) => `/providers/${row.id}`}
            noDataMessage='No providers found.'
          />
        </CardContent>
      </Card>
    );
  }
}

RecentProvidersCard.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { user } = state;
  return { user: user || {} };
}

const styled = withStyles(styles)(RecentProvidersCard);
const connected = connect(mapStateToProps)(styled);
export { connected as RecentProvidersCard };
